import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { API_BASE, authHeaders, clearSession, getAuthToken, setSession } from '../api/client';

const SESSION_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

// ── Theme ──────────────────────────────────────────────
const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [dark, setDark] = useState(() => localStorage.getItem('fantastai_theme') === 'dark');

  useEffect(() => {
    const cls = dark ? 'dark' : 'light';
    document.body.className = cls;
    document.documentElement.className = cls;
    localStorage.setItem('fantastai_theme', cls);
  }, [dark]);

  return (
    <ThemeContext.Provider value={{ dark, setDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);

// ── Auth ───────────────────────────────────────────────
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('fantastai_user')); } catch { return null; }
  });
  const timerRef = useRef(null);

  const clearTimer = () => { if (timerRef.current) clearTimeout(timerRef.current); };

  const logout = useCallback(async () => {
    clearTimer();
    // sesija panaikinama ir serveryje (ne tik naršyklėje)
    if (getAuthToken()) {
      try {
        await fetch(`${API_BASE}/auth/logout`, { method: 'POST', headers: await authHeaders(), keepalive: true });
      } catch {}
    }
    setUser(null);
    localStorage.removeItem('fantastai_user');
    clearSession();
    window.location.href = '/';
  }, []); // eslint-disable-line

  const resetTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(logout, SESSION_TIMEOUT_MS);
  }, [logout]);

  const login = useCallback((userData) => {
    // žetonai laikomi atskirai (api/client), vartotojo objekte jų nesaugome
    const { token, csrf_token, ...profile } = userData;
    if (token) setSession({ token, csrf: csrf_token || null });
    else if (csrf_token) setSession({ csrf: csrf_token });
    setUser(profile);
    localStorage.setItem('fantastai_user', JSON.stringify(profile));
    resetTimer();
  }, [resetTimer]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      login(res.data);
    } catch {}
  }, [login]);

  useEffect(() => {
    if (!user) { clearTimer(); return; }
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      clearTimer();
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, resetTimer]); // eslint-disable-line

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser, isAdmin: user?.role === 'admin' }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// ── Data ───────────────────────────────────────────────
const DataContext = createContext();

const normClub = c => ({
  ...c,
  youtube: c.youtube_url ?? c.youtube ?? null,
  members: c.members ?? 0,
  rating: c.rating ?? 0,
  tags: Array.isArray(c.tags) ? c.tags : [],
  projects: Array.isArray(c.projects) ? c.projects : [],
});

const normEvent = e => ({
  ...e,
  youtube: e.youtube_url ?? e.youtube ?? null,
  maxParticipants: e.max_participants ?? e.maxParticipants ?? 100,
  participants: e.participants ?? 0,
  organizer: e.organizer ?? '',
  rating: e.rating ?? 0,
  tags: Array.isArray(e.tags) ? e.tags : [],
});

export function DataProvider({ children }) {
  const [clubs, setClubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/clubs').catch(() => ({ data: [] })),
      api.get('/events').catch(() => ({ data: [] })),
      api.get('/news').catch(() => ({ data: [] })),
    ]).then(([c, ev, n]) => {
      if (c.data.length)  setClubs(c.data.map(normClub));
      if (ev.data.length) setEvents(ev.data.map(normEvent));
      if (n.data.length)  setNews(n.data.map(item => ({ ...item, tags: Array.isArray(item.tags) ? item.tags : [] })));
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <DataContext.Provider value={{ clubs, events, news, loading, refresh }}>
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => useContext(DataContext);
